import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { FileDown, ArrowLeft, Loader2, ChevronRight, Sparkles, Send, RefreshCw } from "lucide-react";

/**
 * Admin — view the derived AI Visibility Audit for any client with a
 * submitted intake, and download it as a PDF via WeasyPrint.
 *
 * Deep-linkable: /reports/ai-visibility?client_id=<uuid> auto-loads that client.
 */
export default function AIVisibilityAuditView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialClient = searchParams.get("client_id") || "";
  const [intakes, setIntakes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialClient);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/intakes");
        setIntakes((data.intakes || []).filter((i) => i.status === "submitted"));
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedClient) { setReport(null); return; }
    setLoading(true);
    setReport(null);
    api.get(`/reports/ai-visibility/${selectedClient}`)
      .then(({ data }) => setReport(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const pick = (id) => {
    setSelectedClient(id);
    setSearchParams({ client_id: id });
  };

  const downloadPDF = async () => {
    if (!selectedClient) return;
    setDownloading(true);
    try {
      const res = await api.get(`/reports/ai-visibility/${selectedClient}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = (report?.client?.business_name || "audit").replace(/\s+/g, "_");
      a.download = `AI-Visibility-Audit-${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDownloading(false); }
  };

  const sendToClient = async () => {
    if (!selectedClient) return;
    if (!window.confirm(`Email the audit PDF directly to ${report?.client?.email}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/reports/ai-visibility/${selectedClient}/send-to-client`);
      toast.success(`PDF emailed to ${data.sent_to}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const regenerateSummary = async () => {
    if (!selectedClient) return;
    setRegenerating(true);
    try {
      await api.post(`/reports/ai-visibility/${selectedClient}/regenerate-summary`);
      toast.success("Executive summary regenerated.");
      // Reload the report
      const { data } = await api.get(`/reports/ai-visibility/${selectedClient}`);
      setReport(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRegenerating(false); }
  };

  const goalLabel = useMemo(() => ({
    more_phone_calls: "More phone calls",
    more_website_visits: "More website visits",
    more_bookings: "More bookings",
    just_want_to_know: "Understand current standing",
  }[report?.audit?.primary_goal] || report?.audit?.primary_goal || "—"), [report]);

  return (
    <div className="p-10" data-testid="ai-visibility-audit-page">
      <Toaster position="top-right" richColors />
      <button onClick={() => navigate("/reports")}
              className="text-neutral-500 hover:text-black text-sm flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to reports
      </button>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Reports</div>
          <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-[#E24E1B]" /> AI Visibility Audit
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Personalized visibility reports for every client who&apos;s submitted an intake.
            One click downloads a branded PDF ready to send.
          </p>
        </div>
        {selectedClient && report && (
          <div className="flex flex-wrap gap-2">
            <button onClick={sendToClient} disabled={sending || !report?.client?.email}
                    data-testid="ai-audit-send-to-client"
                    className="lf-btn-outline inline-flex items-center gap-2 text-sm"
                    title={report?.client?.email ? `Email PDF to ${report.client.email}` : "No client email on file"}>
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send to client"}
            </button>
            <button onClick={downloadPDF} disabled={downloading}
                    data-testid="ai-audit-download-pdf"
                    className="lf-btn-primary inline-flex items-center gap-2">
              <FileDown className="w-4 h-4" /> {downloading ? "Rendering PDF…" : "Download PDF"}
            </button>
          </div>
        )}
      </div>

      {intakes.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-8 text-center text-neutral-500">
          No submitted intakes yet. Once a client fills out their intake form (via magic link), their audit will appear here.
          <div className="mt-4"><Link to="/intakes" className="lf-btn-outline">Go to Intakes</Link></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          <div className="bg-white border border-neutral-200 max-h-[70vh] overflow-y-auto">
            <div className="text-xs uppercase tracking-widest px-4 py-3 bg-neutral-50 border-b border-neutral-200">
              Submitted intakes ({intakes.length})
            </div>
            {intakes.map((i) => (
              <button key={i.id}
                      onClick={() => pick(i.client_id)}
                      data-testid={`ai-audit-pick-${i.client_id}`}
                      className={`w-full text-left p-3 border-b border-neutral-100 hover:bg-neutral-50 flex items-center justify-between gap-2 ${selectedClient === i.client_id ? "bg-orange-50 border-l-4 border-l-[#E24E1B]" : ""}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{i.client_business_name}</div>
                  <div className="text-[11px] text-neutral-500 font-mono truncate">{i.client_email || "—"}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
              </button>
            ))}
          </div>

          <div>
            {!selectedClient && (
              <div className="bg-white border border-neutral-200 p-8 text-center text-neutral-500">
                Select a client from the list to view their audit.
              </div>
            )}
            {selectedClient && loading && (
              <div className="bg-white border border-neutral-200 p-16 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#E24E1B] mx-auto" />
              </div>
            )}
            {selectedClient && !loading && report && (
              <div className="space-y-6" data-testid="ai-audit-report">
                <div className="bg-[#0F2340] text-white p-6">
                  <div className="text-xs uppercase tracking-widest text-[#E24E1B] mb-2">Report for</div>
                  <div className="font-display text-3xl font-bold">{report.client.business_name}</div>
                  <div className="text-sm text-neutral-300 mt-1">
                    {[report.client.industry, report.client.city].filter(Boolean).join(" · ")}
                    {report.intake.submitted_at && <> · submitted {new Date(report.intake.submitted_at).toLocaleDateString()}</>}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Kpi label="Primary goal" value={goalLabel} />
                  <Kpi label="Google Biz claimed" value={report.audit.google_business_claimed ? "Yes ✓" : "No"} />
                  <Kpi label="Recommendations" value={report.audit.recommendations.length} />
                </div>

                {/* AI-drafted executive summary */}
                <div className="bg-gradient-to-br from-orange-50 to-white border-l-4 border-[#E24E1B] p-5" data-testid="ai-audit-executive-summary">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-widest text-[#E24E1B] font-semibold flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5" /> Executive summary
                    </div>
                    <button onClick={regenerateSummary} disabled={regenerating}
                            data-testid="ai-audit-regen-summary"
                            className="text-xs text-neutral-500 hover:text-black flex items-center gap-1"
                            title="Regenerate via Claude">
                      <RefreshCw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Regenerating…" : "Regenerate"}
                    </button>
                  </div>
                  {report.audit.executive_summary ? (
                    <div className="space-y-3 text-sm text-neutral-800 leading-relaxed">
                      {report.audit.executive_summary.split(/\n+/).filter((p) => p.trim()).map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-500 italic">
                      Executive summary is being generated by Claude in the background. Refresh in a few seconds, or click <em>Regenerate</em> to force it now.
                    </div>
                  )}
                </div>

                <Section title="Top questions your customers ask">
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    {report.audit.top_search_queries.map((q, i) => <li key={i}>{q}</li>)}
                    {report.audit.top_search_queries.length === 0 && <li className="italic text-neutral-500">None provided.</li>}
                  </ul>
                </Section>

                <Section title="Competitors">
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    {report.audit.competitors.map((c, i) => <li key={i}>{c}</li>)}
                    {report.audit.competitors.length === 0 && <li className="italic text-neutral-500">None listed.</li>}
                  </ul>
                </Section>

                <Section title="Where they currently market">
                  <div className="flex flex-wrap gap-2">
                    {(report.intake.answers.current_marketing || []).map((m) => (
                      <span key={m} className="text-xs bg-neutral-100 border border-neutral-200 px-2 py-1">{m}</span>
                    ))}
                    {(report.intake.answers.current_marketing || []).length === 0 && (
                      <span className="italic text-neutral-500 text-sm">Nothing yet.</span>
                    )}
                  </div>
                </Section>

                <Section title="Prioritized recommendations">
                  <div className="divide-y divide-neutral-200">
                    {report.audit.recommendations.map((r, i) => (
                      <div key={i} className="py-3 flex gap-3">
                        <span className={`text-[10px] font-bold px-2 py-1 h-fit whitespace-nowrap border ${
                          r.priority === "P0" ? "bg-red-50 border-red-200 text-red-800" :
                          r.priority === "P1" ? "bg-amber-50 border-amber-200 text-amber-800" :
                          "bg-blue-50 border-blue-200 text-blue-800"
                        }`}>{r.priority}</span>
                        <div>
                          <div className="font-semibold">{r.title}</div>
                          <div className="text-sm text-neutral-600 mt-1">{r.why}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-neutral-200 p-4">
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl font-bold text-[#0F2340] mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <h3 className="font-display text-lg font-bold mb-2">{title}</h3>
      {children}
    </div>
  );
}
